// src/components/admin/EmployeeDialog.jsx
import React, { useState, useEffect } from 'react';
import { X, Eye, EyeOff } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { translations } from '../../utils/translations';
import { storeApi } from '../../api';

const EmployeeDialog = ({ employee, onClose, onSubmit }) => {
  const { language, currentUser } = useApp();
  const t = translations[language];

  const isHQ = currentUser?.store_type === 'headquarters';
  const isManager = currentUser?.role === 'manager';

  const [formData, setFormData] = useState({
    username: '',
    name: '',
    password: '',
    role: 'cashier',
    status: 'active',
    store_id: currentUser?.store_id || ''
  });

  const [stores, setStores] = useState([]);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (isHQ) loadStores();
  }, []);

  useEffect(() => {
    if (employee) {
      setFormData({
        username: employee.username || '',
        name: employee.name || '',
        password: '',
        role: employee.role || 'cashier',
        status: employee.status || 'active',
        store_id: employee.store_id || currentUser?.store_id || ''
      });
    }
  }, [employee]);

  const loadStores = async () => {
    try {
      const res = await storeApi.getList();
      if (res.success) setStores(res.data);
    } catch (err) {
      console.error('加载门店失败:', err);
    }
  };

  const handleStoreChange = (store_id) => {
    const store = stores.find(s => s.id === parseInt(store_id));
    const newForm = { ...formData, store_id };
    // 切换到分店时，如果当前角色是 admin 则重置为 cashier
    if (store?.type === 'branch' && newForm.role === 'admin') {
      newForm.role = 'cashier';
    }
    setFormData(newForm);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = { ...formData };
    if (employee && !submitData.password) delete submitData.password;
    onSubmit(submitData);
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  // 判断当前选择的门店是否是分店
  const selectedStore = isHQ
    ? stores.find(s => s.id === parseInt(formData.store_id))
    : null;
  const isSelectedBranch = selectedStore?.type === 'branch';

  // 角色选项：店长只能选收银员；总店管理员根据选择的门店决定
  const roleOptions = isManager
    ? [
        { value: 'cashier', label: { zh: '收银员', en: 'Cashier', km: 'អ្នកគិតលុយ' } }
      ]
    : isSelectedBranch
      ? [
          { value: 'cashier', label: { zh: '收银员', en: 'Cashier', km: 'អ្នកគិតលុយ' } },
          { value: 'manager', label: { zh: '店长', en: 'Manager', km: 'អ្នកគ្រប់គ្រងហាង' } }
        ]
      : [
          { value: 'cashier', label: { zh: '收银员', en: 'Cashier', km: 'អ្នកគិតលុយ' } },
          { value: 'manager', label: { zh: '店长', en: 'Manager', km: 'អ្នកគ្រប់គ្រងហាង' } },
          { value: 'admin',   label: { zh: '管理员', en: 'Administrator', km: 'អ្នកគ្រប់គ្រង' } }
        ];

  const lang = language === 'en' ? 'en' : language === 'km' ? 'km' : 'zh';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b flex justify-between items-center">
          <h3 className="text-xl font-bold">
            {employee
              ? (language === 'en' ? 'Edit Employee' : language === 'km' ? 'កែសម្រួលបុគ្គលិក' : '编辑员工')
              : (language === 'en' ? 'Add Employee' : language === 'km' ? 'បន្ថែមបុគ្គលិក' : '添加员工')}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">

          {/* 所属门店 - 仅总店管理员可见 */}
          {isHQ && (
            <div>
              <label className="block text-sm font-medium mb-2">
                {language === 'en' ? 'Store *' : language === 'km' ? 'ហាង *' : '所属门店 *'}
              </label>
              <select
                value={formData.store_id}
                onChange={(e) => handleStoreChange(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">
                  {language === 'en' ? 'Select store' : language === 'km' ? 'ជ្រើសរើសហាង' : '请选择门店'}
                </option>
                {stores.map(store => (
                  <option key={store.id} value={store.id}>
                    {store.name}{store.type === 'headquarters' ? '（总店）' : ''}
                  </option>
                ))}
              </select>
              {isSelectedBranch && (
                <p className="text-xs text-orange-500 mt-1">
                  {language === 'en' ? 'Branch store: only Manager or Cashier roles available' : '分店只能设置店长或收银员'}
                </p>
              )}
            </div>
          )}

          {/* 用户名 */}
          <div>
            <label className="block text-sm font-medium mb-2">
              {language === 'en' ? 'Username *' : language === 'km' ? 'ឈ្មោះអ្នកប្រើប្រាស់ *' : '用户名 *'}
            </label>
            <input
              type="text"
              value={formData.username}
              onChange={(e) => handleChange('username', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder={language === 'en' ? 'Enter username' : '输入用户名'}
              required
              disabled={!!employee}
            />
            {employee && (
              <p className="text-xs text-gray-500 mt-1">
                {language === 'en' ? 'Username cannot be changed' : '用户名不可修改'}
              </p>
            )}
          </div>

          {/* 姓名 */}
          <div>
            <label className="block text-sm font-medium mb-2">
              {language === 'en' ? 'Full Name' : language === 'km' ? 'ឈ្មោះពេញ' : '姓名'}
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder={language === 'en' ? 'Enter full name' : '输入姓名'}
            />
          </div>

          {/* 密码 */}
          <div>
            <label className="block text-sm font-medium mb-2">
              {employee
                ? (language === 'en' ? 'New Password (leave blank to keep current)' : '新密码（留空则不修改）')
                : (language === 'en' ? 'Password *' : language === 'km' ? 'ពាក្យសម្ងាត់ *' : '密码 *')}
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                className="w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder={language === 'en' ? 'Enter password' : '输入密码'}
                required={!employee}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {!employee && (
              <p className="text-xs text-gray-500 mt-1">
                {language === 'en' ? 'At least 6 characters' : '至少6个字符'}
              </p>
            )}
          </div>

          {/* 角色 */}
          <div>
            <label className="block text-sm font-medium mb-2">
              {language === 'en' ? 'Role *' : language === 'km' ? 'តួនាទី *' : '角色 *'}
            </label>
            <select
              value={formData.role}
              onChange={(e) => handleChange('role', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              required
              disabled={isManager}
            >
              {roleOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label[lang]}
                </option>
              ))}
            </select>
          </div>

          {/* 状态 */}
          <div>
            <label className="block text-sm font-medium mb-2">
              {language === 'en' ? 'Status *' : language === 'km' ? 'ស្ថានភាព *' : '状态 *'}
            </label>
            <select
              value={formData.status}
              onChange={(e) => handleChange('status', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="active">{language === 'en' ? 'Active' : language === 'km' ? 'សកម្ម' : '活跃'}</option>
              <option value="inactive">{language === 'en' ? 'Inactive' : language === 'km' ? 'អសកម្ម' : '停用'}</option>
            </select>
          </div>

          {/* 按钮 */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t.cancel}
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {employee ? t.save : (language === 'en' ? 'Add' : language === 'km' ? 'បន្ថែម' : '添加')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmployeeDialog;